import { useState, useEffect } from 'react';

interface Scenario {
  id: string;
  name: string;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  conditions: string[];
  active: boolean;
  matchCount: number;
}

const STORAGE_KEY = 'cns_risk_scenarios';

const DEFAULT_SCENARIOS: Scenario[] = [
  {
    id: '1', name: 'Touriste suspect',
    description: 'Touriste avec comportement inhabituel et documents douteux',
    severity: 'high',
    conditions: ['Séjour de courte durée (< 3 jours)', "Changements fréquents d'hôtel", 'Documents avec anomalies', 'Paiement en espèces uniquement'],
    active: true, matchCount: 12,
  },
  {
    id: '2', name: 'Résidence prolongée non déclarée',
    description: 'Personne restant au-delà de la durée autorisée du visa',
    severity: 'critical',
    conditions: ['Séjour > 90 jours', 'Visa touristique', 'Pas de visa de travail', 'Même hôtel pendant toute la durée'],
    active: true, matchCount: 5,
  },
  {
    id: '3', name: 'Activité commerciale non autorisée',
    description: 'Indications d\'activité commerciale avec visa touristique',
    severity: 'high',
    conditions: ['Multiples visiteurs dans la chambre', 'Livraisons fréquentes', 'Visa touristique', 'Séjour prolongé'],
    active: true, matchCount: 8,
  },
  {
    id: '4', name: 'Groupe organisé suspect',
    description: 'Groupe de personnes avec patterns similaires',
    severity: 'critical',
    conditions: ['Arrivée groupée (même vol)', 'Même nationalité', 'Réservations simultanées', 'Contacts fréquents entre membres'],
    active: true, matchCount: 3,
  },
  {
    id: '5', name: 'Document frauduleux',
    description: 'Suspicion de falsification de documents',
    severity: 'critical',
    conditions: ['Échec de validation OCR', 'Incohérences dans les données', 'Photo ne correspond pas', 'Numéro de document invalide'],
    active: true, matchCount: 7,
  },
];

const SEVERITIES: { value: Scenario['severity']; label: string }[] = [
  { value: 'low', label: 'Faible' },
  { value: 'medium', label: 'Moyen' },
  { value: 'high', label: 'Élevé' },
  { value: 'critical', label: 'Critique' },
];

function RiskScenariosPage() {
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingScenario, setEditingScenario] = useState<Scenario | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formSeverity, setFormSeverity] = useState<Scenario['severity']>('medium');
  const [formConditions, setFormConditions] = useState('');
  const [formActive, setFormActive] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try { setScenarios(JSON.parse(stored)); } catch { setScenarios(DEFAULT_SCENARIOS); }
    } else {
      setScenarios(DEFAULT_SCENARIOS);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_SCENARIOS));
    }
  }, []);

  const saveScenarios = (updated: Scenario[]) => {
    setScenarios(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  };

  const openAddModal = () => {
    setEditingScenario(null);
    setFormName(''); setFormDescription(''); setFormSeverity('medium');
    setFormConditions(''); setFormActive(true);
    setShowModal(true);
  };

  const openEditModal = (s: Scenario) => {
    setEditingScenario(s);
    setFormName(s.name); setFormDescription(s.description);
    setFormSeverity(s.severity); setFormConditions(s.conditions.join('\n'));
    setFormActive(s.active);
    setShowModal(true);
  };

  const handleSave = () => {
    if (!formName.trim() || !formDescription.trim()) return;
    const conditions = formConditions.split('\n').map(c => c.trim()).filter(Boolean);
    if (editingScenario) {
      const updated = scenarios.map(s => s.id === editingScenario.id ? {
        ...s, name: formName.trim(), description: formDescription.trim(),
        severity: formSeverity, conditions, active: formActive,
      } : s);
      saveScenarios(updated);
    } else {
      const newScenario: Scenario = {
        id: Date.now().toString(), name: formName.trim(),
        description: formDescription.trim(), severity: formSeverity,
        conditions, active: formActive, matchCount: 0,
      };
      saveScenarios([...scenarios, newScenario]);
    }
    setShowModal(false);
  };

  const handleDelete = (id: string) => {
    saveScenarios(scenarios.filter(s => s.id !== id));
    setConfirmDelete(null);
  };

  const toggleActive = (id: string) => {
    saveScenarios(scenarios.map(s => s.id === id ? { ...s, active: !s.active } : s));
  };

  const getSeverityColor = (severity: string) => {
    const colors: Record<string, string> = {
      low: 'bg-green-100 text-green-800 border-green-300',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      high: 'bg-orange-100 text-orange-800 border-orange-300',
      critical: 'bg-red-100 text-red-800 border-red-300',
    };
    return colors[severity] || colors.low;
  };

  const getSeverityIcon = (severity: string) => {
    if (severity === 'critical') return '🔴';
    if (severity === 'high') return '🟠';
    if (severity === 'medium') return '🟡';
    return '🟢';
  };

  const getSeverityLabel = (severity: string) =>
    SEVERITIES.find(s => s.value === severity)?.label || severity;

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">⚠️ Scénarios de Risque</h1>
            <p className="mt-2 text-gray-600">Gestion des scénarios prédéfinis pour la détection automatique</p>
          </div>
          <button onClick={openAddModal}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Ajouter un scénario
          </button>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Total scénarios</div>
            <div className="text-2xl font-bold text-gray-900">{scenarios.length}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Actifs</div>
            <div className="text-2xl font-bold text-green-600">{scenarios.filter(s => s.active).length}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Critiques</div>
            <div className="text-2xl font-bold text-red-600">{scenarios.filter(s => s.severity === 'critical' && s.active).length}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Correspondances totales</div>
            <div className="text-2xl font-bold text-blue-600">{scenarios.reduce((s, sc) => s + sc.matchCount, 0)}</div>
          </div>
        </div>

        {/* Liste des scénarios */}
        <div className="grid grid-cols-1 gap-6">
          {scenarios.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              Aucun scénario configuré. Cliquez sur "Ajouter un scénario" pour commencer.
            </div>
          ) : (
            scenarios.map((scenario) => (
              <div key={scenario.id} className={`bg-white shadow rounded-lg overflow-hidden border-l-4 ${getSeverityColor(scenario.severity)} ${!scenario.active ? 'opacity-60' : ''}`}>
                <div className="px-6 py-4">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="text-2xl">{getSeverityIcon(scenario.severity)}</span>
                        <h3 className="text-xl font-bold text-gray-900">{scenario.name}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${scenario.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                          {scenario.active ? 'ACTIF' : 'INACTIF'}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(scenario.severity)}`}>
                          {getSeverityLabel(scenario.severity)}
                        </span>
                      </div>
                      <p className="text-gray-600 mb-4">{scenario.description}</p>
                    </div>
                    <div className="ml-4 text-right">
                      <div className="text-3xl font-bold text-blue-600">{scenario.matchCount}</div>
                      <div className="text-xs text-gray-500">correspondances</div>
                    </div>
                  </div>

                  {scenario.conditions.length > 0 && (
                    <div className="border-t border-gray-200 pt-4 mb-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Conditions de déclenchement :</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {scenario.conditions.map((condition, index) => (
                          <div key={index} className="flex items-start space-x-2">
                            <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-sm text-gray-700">{condition}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-2 border-t border-gray-200 pt-3">
                    <button onClick={() => openEditModal(scenario)} className="px-3 py-1 text-sm bg-blue-50 text-blue-700 rounded hover:bg-blue-100 transition">
                      Modifier
                    </button>
                    <button onClick={() => toggleActive(scenario.id)} className={`px-3 py-1 text-sm rounded transition ${scenario.active ? 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}>
                      {scenario.active ? 'Désactiver' : 'Activer'}
                    </button>
                    {confirmDelete === scenario.id ? (
                      <span className="flex items-center gap-2">
                        <span className="text-sm text-red-600">Confirmer ?</span>
                        <button onClick={() => handleDelete(scenario.id)} className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700">Oui</button>
                        <button onClick={() => setConfirmDelete(null)} className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300">Non</button>
                      </span>
                    ) : (
                      <button onClick={() => setConfirmDelete(scenario.id)} className="px-3 py-1 text-sm bg-red-50 text-red-700 rounded hover:bg-red-100 transition">
                        Retirer
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Info box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Détection automatique</h3>
              <p className="mt-2 text-sm text-blue-700">
                Les scénarios sont évalués automatiquement lors de chaque check-in. Lorsque toutes les conditions d'un scénario sont remplies, une alerte est générée avec le niveau de sévérité approprié.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Ajouter/Modifier */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingScenario ? 'Modifier le scénario' : 'Ajouter un scénario'}
              </h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                <input type="text" value={formName} onChange={e => setFormName(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nom du scénario" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea value={formDescription} onChange={e => setFormDescription(e.target.value)} rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Description du scénario" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sévérité *</label>
                <select value={formSeverity} onChange={e => setFormSeverity(e.target.value as Scenario['severity'])}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  {SEVERITIES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Conditions (une par ligne)</label>
                <textarea value={formConditions} onChange={e => setFormConditions(e.target.value)} rows={5}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Condition 1&#10;Condition 2&#10;Condition 3" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="scenario-active" checked={formActive} onChange={e => setFormActive(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <label htmlFor="scenario-active" className="text-sm text-gray-700">Scénario actif</label>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition">
                Annuler
              </button>
              <button onClick={handleSave}
                disabled={!formName.trim() || !formDescription.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed">
                {editingScenario ? 'Enregistrer' : 'Ajouter'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RiskScenariosPage;
